import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Button, Input, Label, Modal } from './ui';
import { cn } from '../lib/cn';
import { CLASS_PALETTE, nextSuggestedColor } from '../utils/classColors';
import { classesApi } from '../services/annotatorApi';
import { APIClientError } from '../services/apiClient';
import type { Class as ClassModel } from '../types/api';
import { extractQuotaInfo, copyFor } from './QuotaExceededModal';

/**
 * AddClassModal — name + color picker for adding a class to a project.
 *
 * Color flow:
 *   - Auto-pre-selects the next-free preset from CLASS_PALETTE (least-used
 *     in the project so all classes look visually distinct).
 *   - 12 preset swatches in a grid (Tailwind 500-shade family).
 *   - "Custom" expander reveals a hex input for arbitrary colors.
 *
 * Validation:
 *   - Name: 1-60 chars, trimmed (server CHECK enforces 1-60).
 *   - Color: must be `#RRGGBB` (case-insensitive). Empty allowed (server
 *     will return null and the canvas falls back to colorForClassIndex).
 *
 * Server errors mapped via APIClientError:
 *   - `class_name_taken`       → field error on name
 *   - `class_quota_exceeded`   → toast
 *   - `invalid_input`          → field error from `fields[]`
 */
interface AddClassModalProps {
    open: boolean;
    onClose: () => void;
    orgId?: string;
    projectId: string;
    existingClasses: ReadonlyArray<ClassModel>;
    isDarkMode?: boolean;
    /** Called after the class is successfully created. */
    onCreated?: (cls: ClassModel) => void;
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export default function AddClassModal({
    open,
    onClose,
    orgId,
    projectId,
    existingClasses,
    isDarkMode = false,
    onCreated,
}: AddClassModalProps) {
    const { t } = useTranslation('modals');
    const { t: tCommon } = useTranslation('common');
    const { t: tErrors } = useTranslation('errors');

    const suggested = useMemo(
        () => nextSuggestedColor(existingClasses.map((c) => c.color ?? undefined)),
        [existingClasses],
    );

    const [name, setName] = useState('');
    const [color, setColor] = useState<string>(suggested);
    const [customMode, setCustomMode] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [nameError, setNameError] = useState<string | null>(null);
    const [colorError, setColorError] = useState<string | null>(null);
    /** Inline banner copy for the structured `annotation_quota_exceeded`
     *  error — rendered at the top of the existing form instead of a
     *  second modal so the user stays in context (per UX feedback). */
    const [quotaBanner, setQuotaBanner] = useState<{ title: string; body: string } | null>(null);

    // Reset whenever the modal re-opens.
    useEffect(() => {
        if (!open) return;
        setName('');
        setColor(suggested);
        setCustomMode(false);
        setNameError(null);
        setColorError(null);
        setQuotaBanner(null);
        setSubmitting(false);
    }, [open, suggested]);

    function validate(): boolean {
        const trimmed = name.trim();
        let ok = true;
        if (!trimmed) {
            setNameError(tCommon('required_field'));
            ok = false;
        } else if (trimmed.length > 60) {
            setNameError(t('class_name_too_long', { defaultValue: 'Name too long (max 60 chars).' }));
            ok = false;
        } else if (existingClasses.some((c) => c.name.toLowerCase() === trimmed.toLowerCase())) {
            setNameError(tErrors('class_name_taken'));
            ok = false;
        } else {
            setNameError(null);
        }
        if (color && !HEX_RE.test(color)) {
            setColorError(t('class_color_invalid', { defaultValue: 'Color must be #RRGGBB hex.' }));
            ok = false;
        } else {
            setColorError(null);
        }
        return ok;
    }

    async function submit() {
        if (!validate()) return;
        setSubmitting(true);
        try {
            const created = await classesApi.create(
                projectId,
                { name: name.trim(), color: color || undefined },
                orgId,
            );
            toast.success(t('class_created', { defaultValue: 'Class added' }));
            onCreated?.(created);
            onClose();
        } catch (err) {
            // Quota errors render INLINE inside this modal — same
            // location as the other field errors so the user stays in
            // context. copyFor maps {scope, current, limit} to friendly
            // title + body; falls back to a generic line on unknown scope.
            const quota = extractQuotaInfo(err);
            if (quota) {
                setQuotaBanner(
                    copyFor(quota) ?? {
                        title: 'Limit reached',
                        body: `A platform limit was reached (${quota.scope}: ${quota.current} / ${quota.limit}). Contact your administrator.`,
                    },
                );
            } else if (err instanceof APIClientError) {
                if (err.code === 'class_name_taken') {
                    setNameError(tErrors('class_name_taken'));
                } else if (err.code === 'invalid_input') {
                    const fieldErr = err.body.fields?.find((f) => f.field === 'name');
                    if (fieldErr) setNameError(fieldErr.issue);
                    const colorFieldErr = err.body.fields?.find((f) => f.field === 'color');
                    if (colorFieldErr) setColorError(colorFieldErr.issue);
                    if (!fieldErr && !colorFieldErr) toast.error(tErrors('invalid_input'));
                } else {
                    toast.error(err.body.message ?? err.code);
                }
            } else {
                toast.error(tErrors('generic'));
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Modal
            open={open}
            onClose={submitting ? () => {} : onClose}
            title={t('add_class_title')}
            widthClass="max-w-md"
            isDarkMode={isDarkMode}
            footer={
                <>
                    <Button variant="ghost" isDarkMode={isDarkMode} onClick={onClose} disabled={submitting}>
                        {tCommon('cancel')}
                    </Button>
                    <Button variant="success" onClick={() => void submit()} disabled={submitting}>
                        {tCommon('create')}
                    </Button>
                </>
            }
        >
            <div className="space-y-5">
                {/* Quota banner — friendly inline error when the server
                    returns annotation_quota_exceeded. Stays in the same
                    modal (no second dialog) so the user keeps context. */}
                {quotaBanner && (
                    <div
                        role="alert"
                        className={cn(
                            'rounded-lg border p-3',
                            isDarkMode
                                ? 'border-red-800 bg-red-900/20'
                                : 'border-red-200 bg-red-50',
                        )}
                    >
                        <p
                            className={cn(
                                'text-sm font-semibold',
                                isDarkMode ? 'text-red-300' : 'text-red-800',
                            )}
                        >
                            {quotaBanner.title}
                        </p>
                        <p
                            className={cn(
                                'text-xs mt-1',
                                isDarkMode ? 'text-red-300/80' : 'text-red-700',
                            )}
                        >
                            {quotaBanner.body}
                        </p>
                    </div>
                )}

                {/* Name */}
                <div>
                    <Label isDarkMode={isDarkMode} required>
                        {t('class_name_label')}
                    </Label>
                    <Input
                        isDarkMode={isDarkMode}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoFocus
                        error={!!nameError}
                        maxLength={60}
                        placeholder={t('class_name_placeholder', { defaultValue: 'e.g. apple, pedestrian, defect' })}
                    />
                    {nameError && <p className="text-xs text-red-500 mt-1.5">{nameError}</p>}
                </div>

                {/* Color */}
                <div>
                    <Label isDarkMode={isDarkMode}>{t('class_color_label')}</Label>

                    <div className="grid grid-cols-6 gap-2 mt-1">
                        {CLASS_PALETTE.map((c) => {
                            const selected = c === color;
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => { setColor(c); setCustomMode(false); }}
                                    className={cn(
                                        'h-9 w-full rounded-md border-2 transition-all duration-100',
                                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
                                        selected
                                            ? isDarkMode ? 'border-white scale-110' : 'border-gray-900 scale-110'
                                            : isDarkMode ? 'border-gray-700' : 'border-gray-200',
                                    )}
                                    style={{ backgroundColor: c }}
                                    aria-label={`Color ${c}`}
                                    aria-pressed={selected}
                                />
                            );
                        })}
                    </div>

                    {/* Custom expander */}
                    <button
                        type="button"
                        onClick={() => setCustomMode((v) => !v)}
                        className={cn(
                            'text-[12px] mt-2 underline-offset-2 hover:underline',
                            isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700',
                        )}
                    >
                        {customMode
                            ? t('class_color_hide_custom', { defaultValue: 'Hide custom color' })
                            : t('class_color_or_custom')}
                    </button>

                    {customMode && (
                        <div className="flex items-center gap-2 mt-2">
                            <span
                                className="h-9 w-9 rounded-md border shrink-0"
                                style={{
                                    backgroundColor: HEX_RE.test(color) ? color : 'transparent',
                                    borderColor: isDarkMode ? '#475569' : '#d1d5db',
                                }}
                                aria-hidden="true"
                            />
                            <Input
                                isDarkMode={isDarkMode}
                                value={color}
                                onChange={(e) => setColor(e.target.value.toLowerCase())}
                                placeholder="#3b82f6"
                                maxLength={7}
                                error={!!colorError}
                                className="font-mono uppercase"
                            />
                        </div>
                    )}
                    {colorError && <p className="text-xs text-red-500 mt-1.5">{colorError}</p>}
                    {!colorError && !customMode && (
                        <p className={cn('text-xs mt-1.5', isDarkMode ? 'text-gray-500' : 'text-gray-500')}>
                            {t('class_color_auto_picked')}
                        </p>
                    )}
                </div>
            </div>
        </Modal>
    );
}
